/* ============================================================
   LUXE Variant Cards v2 — PDP guided buy-box picker.
   Vanilla, deferred. Reproduces design/pdp-sample-*-v6.html
   behaviour and wires selection to the theme's native variant
   picker (same sync path as the live luxe-variant-cards):
   sets the <select>/radio values + dispatches a bubbling change
   event so the theme updates price, ATC, sticky bar and URL.
   ============================================================ */
(function () {
  'use strict';

  function init(section) {
    if (section.dataset.lvc2Init === '1') return;
    section.dataset.lvc2Init = '1';

    var hasColour = section.dataset.hasColour === 'true';
    var cardsWrap = section.querySelector('.luxe-vc2__cards');
    var cards = cardsWrap ? Array.prototype.slice.call(cardsWrap.querySelectorAll('.luxe-vc2__card')) : [];
    if (!cards.length) { section.style.display = ''; return; }

    var swatchWrap = hasColour ? section.querySelector('.luxe-vc2__swatches') : null;

    /* Condition toggle (New / Resealed) state. currentCond is remembered across
       colour switches so a paired kit re-adopts it when the twin is available. */
    var toggleCards = cards.filter(function (c) { return c.querySelector('.luxe-vc2__cond'); });
    var sectionHasToggle = toggleCards.length > 0;
    var currentCond = 'new';

    /* Inject before the theme's native variant picker (kept hidden by CSS),
       exactly like v1 so the buy-box order is preserved. */
    var nativePicker = document.querySelector('variant-selects') || document.querySelector('variant-radios');
    if (nativePicker && nativePicker.parentNode) {
      nativePicker.parentNode.insertBefore(section, nativePicker);
    }
    document.documentElement.classList.add('luxe-vc2-live');
    section.style.display = '';

    /* Clean up if the section is removed in the theme editor */
    document.addEventListener('shopify:section:unload', function (e) {
      if (e.detail && e.detail.sectionId === section.dataset.sectionId) {
        section.dataset.lvc2Init = '';
      }
    });

    /* ── Desktop-only gallery scroll to the selected variant's media ── */
    function scrollToMedia(mediaId) {
      if (!mediaId) return;
      if (!window.matchMedia('(min-width: 990px)').matches) return; /* mobile: card thumb is the confirmation */
      var nodes = document.querySelectorAll('[data-media-id="' + mediaId + '"]');
      for (var i = 0; i < nodes.length; i++) {
        if (nodes[i].offsetParent !== null) {
          var target = nodes[i].closest('.product__media-item') || nodes[i];
          try { target.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (err) { target.scrollIntoView(); }
          return;
        }
      }
    }

    /* ── Sync a value into a native <select> by value or visible text ── */
    function selectOptionByText(select, text) {
      var i;
      for (i = 0; i < select.options.length; i++) {
        if (select.options[i].value === text) { select.selectedIndex = i; return true; }
      }
      for (i = 0; i < select.options.length; i++) {
        if (select.options[i].textContent.trim() === text) { select.selectedIndex = i; return true; }
      }
      return false;
    }

    /* ── Core: mark the active card + drive the real product form ── */
    function setActive(variantId, userClicked) {
      variantId = String(variantId);
      var activeCard = cards.filter(function (c) { return c.dataset.variantId === variantId; })[0];
      if (!activeCard) return;

      cards.forEach(function (c) {
        var on = c.dataset.variantId === variantId;
        c.classList.toggle('luxe-vc2__card--sel', on);
        c.setAttribute('aria-checked', on);
        c.setAttribute('tabindex', on ? '0' : '-1');
        if (on) { c.classList.remove('luxe-vc2__card--peek'); } /* selection expands its own drawer */
      });

      /* Sync the theme's native picker (re-query — DOM may have re-rendered).
         Scope to the product section we were injected into, so other sections'
         pickers/forms (quick-add, also-like) are never touched. */
      var root = section.closest('.shopify-section') || document;
      var picker = root.querySelector('variant-selects') || root.querySelector('variant-radios');
      if (picker) {
        var opts = [activeCard.dataset.option1, activeCard.dataset.option2, activeCard.dataset.option3];
        var selects = picker.querySelectorAll('select');
        var fieldsets = picker.querySelectorAll('fieldset');
        var dispatchTarget = null;

        if (selects.length) {
          selects.forEach(function (select, idx) {
            if (!opts[idx]) return;
            if (!selectOptionByText(select, opts[idx])) return;
            var old = select.querySelector('option[selected]');
            if (old) old.removeAttribute('selected');
            if (select.selectedOptions[0]) select.selectedOptions[0].setAttribute('selected', 'selected');
            dispatchTarget = select;
          });
        } else if (fieldsets.length) {
          fieldsets.forEach(function (fs, idx) {
            if (!opts[idx]) return;
            var radios = fs.querySelectorAll('input[type="radio"]');
            for (var i = 0; i < radios.length; i++) {
              if (radios[i].value === opts[idx]) { radios[i].checked = true; dispatchTarget = radios[i]; break; }
            }
          });
        }
        if (dispatchTarget) dispatchTarget.dispatchEvent(new Event('change', { bubbles: true }));
      }

      /* Fallback: keep this section's hidden id inputs in step (some ATC forms
         read these). Scoped to root - a document-wide sweep would overwrite the
         variant id in unrelated forms (quick-add cards, other sections). */
      root.querySelectorAll('input[name="id"][type="hidden"]').forEach(function (input) {
        if (input.closest('form')) input.value = variantId;
      });

      if (userClicked) {
        var url = new URL(window.location);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', url);
      }

      if (hasColour && activeCard.dataset.option1) applyColourFilter(activeCard.dataset.option1);
      scrollToMedia(activeCard.dataset.mediaId);
    }

    /* ── Colour filter (visual only; safe to call from setActive) ── */
    function applyColourFilter(colour) {
      if (!colour || !swatchWrap) return;
      var cl = colour.toLowerCase();
      cards.forEach(function (c) {
        c.classList.toggle('is-filtered-out', (c.dataset.colour || '').toLowerCase() !== cl);
      });
      swatchWrap.querySelectorAll('.luxe-vc2__swatch').forEach(function (s) {
        var on = (s.dataset.colour || '').toLowerCase() === cl;
        s.classList.toggle('luxe-vc2__swatch--sel', on);
        s.setAttribute('aria-checked', on);
        s.setAttribute('tabindex', on ? '0' : '-1');
      });
    }

    /* ── Pick the best variant for a newly chosen colour, then select it ── */
    function chooseColour(colour) {
      if (!colour) return;
      /* Remember the kit selected before the filter, so a paired product keeps
         the same kit (and its condition) across a colour switch. */
      var curSel = cards.filter(function (c) { return c.classList.contains('luxe-vc2__card--sel'); })[0];
      var curKit = curSel ? (curSel.dataset.kitKey || '') : '';
      applyColourFilter(colour);
      var cl = colour.toLowerCase();
      var matches = cards.filter(function (c) { return (c.dataset.colour || '').toLowerCase() === cl; });
      var avail = matches.filter(function (c) { return !c.classList.contains('luxe-vc2__card--soldout'); });
      var target = avail[0] || matches[0];
      /* Only deviate from the current (first-available) behaviour when this
         product actually has a condition toggle — otherwise degrade exactly. */
      if (sectionHasToggle && curKit) {
        var sameKit = avail.filter(function (c) { return (c.dataset.kitKey || '') === curKit; })[0];
        if (sameKit) target = sameKit;
      }
      if (!target) return;
      if (sectionHasToggle && target.querySelector('.luxe-vc2__cond')) {
        /* Twin available in this colour → re-adopt the remembered condition. */
        setCond(target, currentCond === 'resealed' ? 'resealed' : 'new', true);
      } else {
        setActive(target.dataset.variantId, true);
      }
    }

    /* ── Card selection (click + keyboard radio pattern) ── */
    function visibleCards() {
      return cards.filter(function (c) { return !c.classList.contains('is-filtered-out'); });
    }
    cards.forEach(function (card) {
      card.addEventListener('click', function (e) {
        if (e.target.closest('[data-peek]') || e.target.closest('a') || e.target.closest('.luxe-vc2__cond')) return; /* peek + drawer links + the condition toggle handle themselves */
        if (card.classList.contains('luxe-vc2__card--soldout')) return;
        setActive(card.dataset.variantId, true);
      });
      card.addEventListener('keydown', function (e) {
        if (e.target.closest('.luxe-vc2__cond')) return; /* toggle owns its own keys */
        var k = e.key;
        if (k === 'Enter' || k === ' ') {
          if (e.target.closest('[data-peek]')) return;
          e.preventDefault();
          if (!card.classList.contains('luxe-vc2__card--soldout')) setActive(card.dataset.variantId, true);
          return;
        }
        if (['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft', 'Home', 'End'].indexOf(k) === -1) return;
        e.preventDefault();
        var vis = visibleCards();
        var idx = vis.indexOf(card);
        if (idx < 0) return;
        var next;
        if (k === 'Home') next = 0;
        else if (k === 'End') next = vis.length - 1;
        else if (k === 'ArrowUp' || k === 'ArrowLeft') next = idx === 0 ? vis.length - 1 : idx - 1;
        else next = idx === vis.length - 1 ? 0 : idx + 1;
        var nc = vis[next];
        if (!nc) return;
        nc.focus();
        if (!nc.classList.contains('luxe-vc2__card--soldout')) setActive(nc.dataset.variantId, true);
      });
    });

    /* ── Colour swatches ── */
    if (swatchWrap) {
      swatchWrap.addEventListener('click', function (e) {
        var s = e.target.closest('.luxe-vc2__swatch');
        if (!s || s.classList.contains('luxe-vc2__swatch--sel')) return;
        chooseColour(s.dataset.colour);
      });
      swatchWrap.addEventListener('keydown', function (e) {
        if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].indexOf(e.key) === -1) return;
        e.preventDefault();
        var sw = Array.prototype.slice.call(swatchWrap.querySelectorAll('.luxe-vc2__swatch'));
        if (!sw.length) return;
        var cur = sw.findIndex(function (s) { return s.classList.contains('luxe-vc2__swatch--sel'); });
        if (cur < 0) cur = 0;
        var n;
        if (e.key === 'Home') n = 0;
        else if (e.key === 'End') n = sw.length - 1;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') n = cur === 0 ? sw.length - 1 : cur - 1;
        else n = cur === sw.length - 1 ? 0 : cur + 1;
        chooseColour(sw[n].dataset.colour);
        sw[n].focus();
      });
    }

    /* ── "What's inside" peek (does NOT select) ── */
    section.querySelectorAll('[data-peek]').forEach(function (p) {
      p.addEventListener('click', function (e) {
        e.stopPropagation();
        var card = p.closest('.luxe-vc2__card');
        if (!card || card.classList.contains('luxe-vc2__card--sel')) return;
        var open = card.classList.toggle('luxe-vc2__card--peek');
        p.textContent = open ? (p.dataset.peekHide || 'Hide contents') : (p.dataset.peekShow || "What's inside");
      });
    });

    /* ── Condition toggle (New / Resealed) ──
       Each toggle card carries its NEW state (data-new-*) and its resealed twin
       (data-twin-*). Switching condition mutates the card's own selection dataset
       (variantId + options that setActive reads) and its visible price/description,
       then calls setActive() — the SAME path a card click uses — so ATC, price,
       sticky bar and URL update with no reload. Toggling also selects the card. */
    function ensureSub(card) {
      var sub = card.querySelector('.luxe-vc2__sub');
      if (sub) return sub;
      var mid = card.querySelector('.luxe-vc2__mid');
      if (!mid) return null;
      sub = document.createElement('small');
      sub.className = 'luxe-vc2__sub';
      var nm = mid.querySelector('.luxe-vc2__name');
      if (nm) nm.insertAdjacentElement('afterend', sub); else mid.insertBefore(sub, mid.firstChild);
      return sub;
    }

    function applyCondDesc(card, cond) {
      var d = card.dataset;
      var sub = card.querySelector('.luxe-vc2__sub');
      var trust = card.querySelector('.luxe-vc2__trust');
      /* Stacked-description card (replace mode): both sides are in the DOM in
         one grid cell, so switching = flipping a class. The hidden side keeps
         its height - the card never reflows on toggle (Dan 07-11). */
      if (sub && sub.classList.contains('luxe-vc2__sub--stack')) {
        sub.classList.toggle('is-resealed', cond === 'resealed');
        if (trust) trust.classList.remove('is-show');
        return;
      }
      if (cond === 'resealed' && d.twinDescMode === 'replace') {
        sub = ensureSub(card);
        if (sub) { sub.textContent = d.twinDesc || ''; sub.style.display = d.twinDesc ? '' : 'none'; }
        if (trust) trust.classList.remove('is-show');
      } else if (cond === 'resealed') {
        /* trust mode: keep the kit description, reveal the trust line */
        if (sub) { sub.textContent = d.newDesc || ''; sub.style.display = d.newDesc ? '' : 'none'; }
        if (trust) trust.classList.add('is-show');
      } else {
        if (sub) { sub.textContent = d.newDesc || ''; sub.style.display = d.newDesc ? '' : 'none'; }
        if (trust) trust.classList.remove('is-show');
      }
    }

    function setCond(card, cond, userClicked) {
      var d = card.dataset;
      if (cond === 'resealed' && !d.twinVariantId) cond = 'new';
      var p = cond === 'resealed' ? 'twin' : 'new';

      /* Point the card's selection dataset at the chosen side. */
      card.dataset.variantId = d[p + 'VariantId'] || card.dataset.variantId;
      card.dataset.option1 = d[p + 'Option1'] || '';
      card.dataset.option2 = d[p + 'Option2'] || '';
      card.dataset.option3 = d[p + 'Option3'] || '';
      if (d[p + 'MediaId']) card.dataset.mediaId = d[p + 'MediaId'];

      /* Price row: price, compare-at strike, SAVE pill. */
      var priceB = card.querySelector('.luxe-vc2__price b');
      var strike = card.querySelector('.luxe-vc2__price s');
      var save = card.querySelector('.luxe-vc2__save');
      var hasSav = d[p + 'HasSavings'] === 'true';
      if (priceB) priceB.textContent = d[p + 'Price'] || priceB.textContent;
      if (strike) {
        if (hasSav) { strike.textContent = d[p + 'Compare'] || ''; strike.style.display = ''; }
        else strike.style.display = 'none';
      }
      if (save) {
        if (hasSav) { save.textContent = d[p + 'Save'] || ''; save.classList.remove('luxe-vc2__save--empty'); }
        else save.classList.add('luxe-vc2__save--empty');
      }

      applyCondDesc(card, cond);

      /* Segment button states — selected side drops its price (aria + visual). */
      card.querySelectorAll('.luxe-vc2__cond-btn').forEach(function (b) {
        var on = b.dataset.cond === cond;
        b.classList.toggle('luxe-vc2__cond-btn--sel', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
        b.setAttribute('tabindex', on ? '0' : '-1');
      });

      currentCond = cond;
      /* Same selection path as a card click (also selects the card if inactive). */
      setActive(card.dataset.variantId, userClicked !== false);
    }

    toggleCards.forEach(function (card) {
      var group = card.querySelector('.luxe-vc2__cond');
      if (!group) return;
      group.addEventListener('click', function (e) {
        var btn = e.target.closest('.luxe-vc2__cond-btn');
        if (!btn) return;
        e.stopPropagation(); /* don't let the card's own click re-fire */
        setCond(card, btn.dataset.cond, true);
      });
      group.addEventListener('keydown', function (e) {
        var k = e.key;
        if (k === 'ArrowLeft' || k === 'ArrowRight' || k === 'ArrowUp' || k === 'ArrowDown') {
          e.preventDefault(); e.stopPropagation();
          var btns = Array.prototype.slice.call(group.querySelectorAll('.luxe-vc2__cond-btn'));
          var cur = btns.filter(function (b) { return b.classList.contains('luxe-vc2__cond-btn--sel'); })[0];
          var idx = btns.indexOf(cur);
          if (idx < 0) idx = 0;
          var next = (k === 'ArrowLeft' || k === 'ArrowUp') ? (idx === 0 ? btns.length - 1 : idx - 1) : (idx === btns.length - 1 ? 0 : idx + 1);
          var nb = btns[next];
          if (nb) { setCond(card, nb.dataset.cond, true); nb.focus(); }
        } else if (k === 'Enter' || k === ' ') {
          var b = e.target.closest('.luxe-vc2__cond-btn');
          if (b) { e.preventDefault(); e.stopPropagation(); setCond(card, b.dataset.cond, true); }
        }
      });
    });

    /* ── Initial selection (URL variant wins over the metafield default) ── */
    var params = new URLSearchParams(window.location.search);
    var initial = params.get('variant') || section.dataset.initialVariant;
    if (hasColour && section.dataset.initialColour) applyColourFilter(section.dataset.initialColour);
    /* Deep-link to a resealed twin: no card owns that id as data-variant-id, so
       find the toggle card whose twin it is and land on the resealed side. */
    var initialStr = initial ? String(initial) : '';
    var twinCard = initialStr ? toggleCards.filter(function (c) { return c.dataset.twinVariantId === initialStr; })[0] : null;
    if (twinCard) setCond(twinCard, 'resealed', false);
    else if (initial) setActive(initial, false);
  }

  function boot() {
    document.querySelectorAll('.luxe-vc2[data-section-id]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  /* Re-init when re-added in the theme editor */
  document.addEventListener('shopify:section:load', boot);
})();
